import React, { useEffect, useState } from 'react';
import { useHistory } from "react-router-dom";
import { FaWhatsapp, FaCcVisa, FaCcMastercard } from 'react-icons/fa';
import { SiGooglepay } from 'react-icons/si';
import { HiOutlineLocationMarker, HiOutlinePencil, HiOutlineShoppingBag, HiOutlineEmojiSad, HiCheck, HiOutlineCreditCard, HiOutlineShieldCheck, HiOutlineClock, HiOutlineEyeOff } from 'react-icons/hi';
import AuthenticationService from '../AuthenticationService';
import CartService from '../CartService';
import OrderService from '../OrderService';
import { loadRazorpayScript, checkoutOptions } from '../../util/razorpay';
import { enabledMethods, defaultMethod, isMobileDevice, METHOD_UPI, METHOD_CARD } from '../../util/paymentMethods';
import '../../css/checkout.css'
import { formatRupee } from '../../components/Price';
import OrderSummary from '../../components/OrderSummary';
import Skeleton from '../../components/Skeleton';
import EmptyState from '../../components/EmptyState';
import Modal from '../../components/Modal';
import CouponBox from '../../components/CouponBox';
import { cartCoupon } from '../../util/coupons';
import { notifyCartUpdated } from '../../util/cartEvents';
import brand, { brandText, whatsappLink } from '../../brand';

const STEPS = ['Review', 'Pay', 'Confirm']
const IS_MOBILE = isMobileDevice()

const themeColor = () =>
    getComputedStyle(document.documentElement).getPropertyValue('--color-primary').trim() || '#6d28d9'

// Checkout: review the cart, then pay with UPI or card in Razorpay Checkout; WhatsApp when neither is on.
// Every path ends on /payment/status/:orderId.
const Checkout = () => {
    const history = useHistory();

    const [cartItems, setCartItems] = useState([])
    const [isLoading, setIsLoading] = useState(true)
    const [loadError, setLoadError] = useState(false)
    const [total, setTotal] = useState(null)
    const [deliveryCharge, setDeliveryCharge] = useState(0)
    const [address, setAddress] = useState('')
    const [finalPrice, setFinalPrice] = useState(0)
    const [hasUnavailable, setHasUnavailable] = useState(false)
    const [coupon, setCoupon] = useState(null)

    // { upi, card } from the admin settings; null while loading
    const [methods, setMethods] = useState(null)
    const [selected, setSelected] = useState(null)
    const [isCreating, setIsCreating] = useState(false)
    const [payError, setPayError] = useState('')
    const [payNotice, setPayNotice] = useState('')
    const [isVerifying, setIsVerifying] = useState(false)

    // WhatsApp "I have paid" confirmation dialog
    const [confirming, setConfirming] = useState(false)
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [submitError, setSubmitError] = useState('')

    const toLogin = (error) => {
        if (error?.response?.status === 401) {
            history.push('/login')
            return true
        }
        return false
    }

    const showCart = (data) => {
        setCartItems(data.cartListView)
        setTotal(data.cartItemTotal)
        setAddress(data.address)
        setDeliveryCharge(data.deliveryCharges)
        setFinalPrice(data.finalPrice)
        setHasUnavailable(!!data.hasUnavailable)
        setCoupon(cartCoupon(data))
    }

    // quiet: refresh the totals in place (e.g. after the server refused a changed total), no skeleton
    const fetchCart = (quiet) => {
        if (!quiet) {
            setIsLoading(true)
            setLoadError(false)
        }
        CartService.getCartItems().then(response => {
            if (response.status) {
                showCart(response.data.data)
                setIsLoading(false)
            }
        }).catch(error => {
            console.log(error)
            if (toLogin(error)) return
            if (quiet) return
            setLoadError(true)
            setIsLoading(false)
        });
    }

    const fetchMethods = () => {
        OrderService.getPaymentMethods().then(res => {
            setMethods(res.data)
            setSelected(defaultMethod(enabledMethods(res.data), IS_MOBILE))
        }).catch(error => {
            if (toLogin(error)) return
            // fail closed: no online payment, the customer can still order on WhatsApp
            setMethods({ upi: false, card: false })
        });
    }

    useEffect(() => {
        AuthenticationService.getUserDetails().catch(error => {
            toLogin(error)
            console.log(error)
        });
        fetchCart()
        fetchMethods()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // warm up the Razorpay script as soon as a method is chosen, so Checkout opens instantly
    useEffect(() => {
        if (selected) loadRazorpayScript()
    }, [selected])

    const goToStatus = (orderId) => {
        notifyCartUpdated()
        history.push('/payment/status/' + encodeURIComponent(orderId))
    }

    const startError = (error, fallback) => {
        setIsCreating(false)
        if (toLogin(error)) return
        setPayError(error?.response?.data?.msg || fallback)
        // the admin may have switched a method off meanwhile, or the total changed (a coupon ended)
        fetchMethods()
        if (error?.response?.status === 400) fetchCart(true)
    }

    // ---------- UPI or card, both in Razorpay Checkout ----------
    const payWithRazorpay = async (method) => {
        setIsCreating(true)
        const [scriptLoaded, orderRes] = await Promise.all([
            loadRazorpayScript(),
            OrderService.createRazorpayOrder(method, Number(finalPrice)).catch(error => ({ error }))
        ])
        if (orderRes.error) {
            startError(orderRes.error, 'We could not start the payment. Please try again.')
            return
        }
        const order = orderRes.data
        if (!scriptLoaded) {
            setIsCreating(false)
            setPayError('The payment window could not load. Check your connection and try again.')
            OrderService.cancelPayment(order.orderId).catch(error => console.log(error))
            return
        }

        const itemCount = cartItems.reduce((sum, e) => sum + Number(e.quantity || 0), 0)
        const rzp = new window.Razorpay(checkoutOptions(order, method, {
            description: `${itemCount} item${itemCount === 1 ? '' : 's'}`,
            themeColor: themeColor(),
            onSuccess: (response) => {
                setIsVerifying(true)
                OrderService.verifyRazorpayPayment(response)
                    .catch(error => console.log(error))
                    // the status page shows the verified result (paid, or failed)
                    .finally(() => goToStatus(order.orderId))
            },
            onDismiss: () => {
                setIsCreating(false)
                setPayNotice('Payment cancelled. Nothing was charged, you can try again.')
                OrderService.cancelPayment(order.orderId).catch(error => console.log(error))
            }
        }))
        rzp.on('payment.failed', (response) => {
            // Checkout stays open so the customer can retry (another UPI app or card)
            setPayError(response?.error?.description || 'The payment failed.')
        })
        rzp.open()
    }

    const pay = () => {
        setPayError('')
        setPayNotice('')
        if (selected) payWithRazorpay(selected)
    }

    // ---------- WhatsApp "I have paid" ----------
    const submitConfirmation = () => {
        setIsSubmitting(true)
        setSubmitError('')
        OrderService.confirmWhatsappOrder(Number(finalPrice)).then(res => {
            setIsSubmitting(false)
            setConfirming(false)
            goToStatus(res.data.orderId)
        }).catch(error => {
            setIsSubmitting(false)
            if (toLogin(error)) return
            setSubmitError(error?.response?.data?.msg || error?.response?.data?.message || 'We could not submit your confirmation. Please try again.')
            if (error?.response?.status === 400) fetchCart(true)
        });
    }

    // Pay while Razorpay Checkout is open; Confirm while the payment is being verified or confirmed
    const currentStep = (isVerifying || confirming) ? 2 : (isCreating ? 1 : 0)

    if (isLoading) {
        return (
            <main className='mv-page mv-checkout' aria-busy='true'>
                <Skeleton width='240px' height={28} />
                <div className='mv-cart-layout'>
                    <div className='mv-cart-items'>
                        <Skeleton height={110} radius='14px' />
                        <Skeleton height={180} radius='14px' />
                    </div>
                    <Skeleton height={220} radius='14px' />
                </div>
            </main>
        )
    }

    if (loadError) {
        return (
            <main className='mv-page'>
                <EmptyState icon={<HiOutlineEmojiSad />} title='We could not load your order'
                    text='Please check your connection and try again.' actionLabel='Try again' onAction={() => fetchCart()} />
            </main>
        )
    }

    if (cartItems == null || cartItems.length === 0) {
        return (
            <main className='mv-page'>
                <EmptyState icon={<HiOutlineShoppingBag />} title='Nothing to pay for yet'
                    text='Your cart is empty. Add some styles you love and come back to checkout.'
                    actionLabel='Start shopping' onAction={() => history.push('/')} />
            </main>
        )
    }

    // a product was hidden after it was added: the server refuses payment until it is removed from the cart
    if (hasUnavailable) {
        return (
            <main className='mv-page'>
                <EmptyState icon={<HiOutlineEyeOff />} title='Some items are no longer available'
                    text='Remove the items that are no longer available from your cart, then come back to pay.'
                    actionLabel='Back to cart' onAction={() => history.push('/cart')} />
            </main>
        )
    }

    const itemCount = cartItems.reduce((sum, e) => sum + Number(e.quantity || 0), 0)
    const options = enabledMethods(methods)
    const noGateway = methods !== null && options.length === 0
    const canPay = !!address && !isCreating && !!selected
    const orderOnWhatsApp = whatsappLink(brandText(brand.whatsappOrderMessage,
        { count: itemCount, total: formatRupee(finalPrice) }))
    const payLabel = selected === METHOD_CARD ? `Pay ${formatRupee(finalPrice)} by card`
        : selected === METHOD_UPI ? `Pay ${formatRupee(finalPrice)} with UPI` : 'Choose how to pay'

    const methodCard = (value, icon, title, sub, badge) => (
        <label key={value} className={selected === value ? 'mv-method selected' : 'mv-method'}>
            <input type='radio' name='pay-method' value={value} checked={selected === value}
                onChange={() => { setSelected(value); setPayError(''); setPayNotice('') }} />
            <span className='mv-method-icon' aria-hidden='true'>{icon}</span>
            <span className='mv-method-text'>
                <span className='mv-method-title'>{title}{badge && <span className='mv-method-badge'>{badge}</span>}</span>
                <span className='mv-method-sub'>{sub}</span>
            </span>
            <span className='mv-method-radio' aria-hidden='true' />
        </label>
    )

    return (
        <main className='mv-page mv-checkout'>
            <ol className='mv-steps' aria-label='Checkout progress'>
                {STEPS.map((label, i) =>
                    <li key={label} className={i < currentStep ? 'done' : (i === currentStep ? 'current' : '')}
                        aria-current={i === currentStep ? 'step' : undefined}>
                        <span className='mv-step-dot'>{i < currentStep ? <HiCheck /> : i + 1}</span>
                        <span className='mv-step-label'>{label}</span>
                    </li>
                )}
            </ol>

            <div className='mv-cart-layout'>
                <div className='mv-cart-items'>
                    <section className='mv-panel'>
                        <div className='mv-panel-head'>
                            <h2 className='mv-panel-title'><HiOutlineLocationMarker /> Delivery address</h2>
                            <button type='button' className='mv-link-btn' onClick={() => history.push({ pathname: '/profile', state: { id: '/payment', editAddress: true } })}>
                                <HiOutlinePencil /> Edit
                            </button>
                        </div>
                        {address ?
                            <p className='mv-address'>{address}</p> :
                            <div className='mv-alert mv-alert-error'>Please add a delivery address before paying.</div>}
                    </section>

                    <section className='mv-panel'>
                        <h2 className='mv-panel-title'>Items ({itemCount})</h2>
                        <ul className='mv-review-list'>
                            {cartItems.map(e =>
                                <li key={e.cartId} className='mv-review-item'>
                                    <img src={e.image} alt='' loading='lazy' />
                                    <div className='mv-review-info'>
                                        <span className='mv-cart-name'>{e.itemName}</span>
                                        <span className='mv-cart-size'>{e.size ? `Size: ${e.size} · ` : ''}Qty: {e.quantity}</span>
                                    </div>
                                    <span className='mv-review-price'>{formatRupee(Number(e.price) * e.quantity)}</span>
                                </li>
                            )}
                        </ul>
                    </section>
                </div>

                <aside className='mv-cart-aside'>
                    <OrderSummary subtotal={total} delivery={deliveryCharge} total={finalPrice} itemCount={itemCount}
                        discount={coupon && coupon.code ? coupon.discount : 0} couponCode={coupon && coupon.code}
                        freeDelivery={!!(coupon && coupon.freeDelivery)} saving={coupon && coupon.code ? coupon.saving : 0}
                        coupon={<CouponBox coupon={coupon} disabled={isCreating}
                            onCart={(data) => { showCart(data); setPayError(''); setPayNotice('') }} />}>
                        {methods === null && <Skeleton height={120} radius='12px' />}

                        {options.length > 0 &&
                            <fieldset className='mv-method-group'>
                                <legend className='mv-method-legend'>Choose how to pay</legend>
                                {options.map(m => m === METHOD_UPI ?
                                    methodCard(METHOD_UPI, <SiGooglepay />, 'UPI',
                                        IS_MOBILE ? 'Google Pay, PhonePe, Paytm or any UPI app' : 'Scan a QR code with Google Pay, PhonePe or any UPI app',
                                        IS_MOBILE ? 'Recommended' : null) :
                                    methodCard(METHOD_CARD, <HiOutlineCreditCard />, 'Credit / Debit card',
                                        <><FaCcVisa aria-label='Visa' /> <FaCcMastercard aria-label='Mastercard' /> RuPay · Secured by Razorpay</>, null))}
                            </fieldset>}

                        {payError && <div className='mv-alert mv-alert-error' role='alert'>{payError}</div>}
                        {payNotice && <div className='mv-alert mv-alert-info' role='status'>{payNotice}</div>}

                        {noGateway ?
                            <div className='mv-whatsapp-order'>
                                <div className='mv-alert mv-alert-info' role='status'>
                                    Online payment isn't available right now. Order on WhatsApp and pay {brand.name} directly.
                                </div>
                                <a className='mv-btn mv-btn-primary mv-btn-block mv-summary-cta' href={orderOnWhatsApp}
                                    target='_blank' rel='noopener noreferrer'>
                                    <FaWhatsapp /> Order on WhatsApp
                                </a>
                                <button type='button' className='mv-btn mv-btn-secondary mv-btn-block' disabled={!address}
                                    onClick={() => { setSubmitError(''); setConfirming(true) }}>
                                    I have paid
                                </button>
                            </div> :
                            methods !== null &&
                            <>
                                <button type='button' className='mv-btn mv-btn-primary mv-btn-block mv-summary-cta' onClick={pay} disabled={!canPay}>
                                    {isCreating ? 'Starting payment…' : payLabel}
                                </button>
                                <p className='mv-pay-hold'>
                                    <HiOutlineClock aria-hidden='true' /> Your items are held for you for 10 minutes while you pay.
                                </p>
                            </>}
                    </OrderSummary>
                </aside>

                {methods !== null && !noGateway &&
                    <div className='mv-checkout-bar'>
                        <div>
                            <span className='mv-checkout-bar-label'>Total</span>
                            <span className='mv-checkout-bar-total'>{formatRupee(finalPrice)}</span>
                            {coupon && coupon.code && coupon.saving > 0 && <span className='mv-checkout-bar-save'>You save {formatRupee(coupon.saving)}</span>}
                        </div>
                        <button type='button' className='mv-btn mv-btn-primary' onClick={pay} disabled={!canPay}>
                            {isCreating ? 'Starting…' : (selected === METHOD_CARD ? 'Pay by card'
                                : selected === METHOD_UPI ? 'Pay with UPI' : 'Choose how to pay')}
                        </button>
                    </div>}
            </div>

            {isVerifying &&
                <div className='mv-modal-overlay' role='status' aria-live='polite'>
                    <div className='mv-modal mv-verifying'>
                        <HiOutlineShieldCheck className='mv-verifying-icon' aria-hidden='true' />
                        <p className='mv-modal-text'>Confirming your payment… please don't close this page.</p>
                    </div>
                </div>}

            {confirming && (
                <Modal
                    title='Confirm your WhatsApp payment'
                    onClose={() => setConfirming(false)}
                    busy={isSubmitting}
                    actions={<>
                        <button type='button' className='mv-btn mv-btn-secondary' onClick={() => setConfirming(false)} disabled={isSubmitting}>
                            Cancel
                        </button>
                        <button type='button' className='mv-btn mv-btn-primary' onClick={submitConfirmation} disabled={isSubmitting}>
                            {isSubmitting ? 'Submitting…' : 'Yes, I have paid'}
                        </button>
                    </>}
                >
                    <p className='mv-modal-text'>
                        Only confirm after you have paid <strong>{formatRupee(finalPrice)}</strong> to {brand.name} on WhatsApp.
                        We will hold your items and confirm the order once we have verified the payment.
                    </p>
                    {submitError && <div className='mv-alert mv-alert-error' role='alert'>{submitError}</div>}
                </Modal>
            )}
        </main>
    );
};

export default Checkout;
